import { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { useDirtyTracking } from "../../settings/defaults/useDirtyTracking";
import { AnswerEditor } from "./AnswerEditor";
import { EditorTopBar } from "./EditorTopBar";
import { SortOrderPanel } from "./SortOrderPanel";
import { VisibilityPanel } from "./VisibilityPanel";

export interface FaqFormValues {
  question: string;
  answer: string;
  sortOrder: number | null;
  showOnFaqPage: boolean;
}

interface Props {
  mode: "create" | "edit";
  initialValues: FaqFormValues;
  isSaving: boolean;
  saveError?: string | null;
  onSave: (values: FaqFormValues) => Promise<void>;
  onDelete?: () => void;
  isDeleting?: boolean;
}

/**
 * Two-column FAQ editor. Question and HugeRTE answer fill the main
 * column; visibility, sort order, and delete stack in the right
 * rail. Pure presentation w.r.t. data — Create/Edit wrappers own
 * the GraphQL.
 */
export function FaqEditor({
  mode,
  initialValues,
  isSaving,
  saveError,
  onSave,
  onDelete,
  isDeleting,
}: Props) {
  const tracking = useDirtyTracking<FaqFormValues>(initialValues);

  const [hydratedKey, setHydratedKey] = useState<string>(
    JSON.stringify(initialValues),
  );
  useEffect(() => {
    const next = JSON.stringify(initialValues);
    if (next !== hydratedKey) {
      tracking.markSaved(initialValues);
      setHydratedKey(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const v = tracking.values;
  const setField = tracking.setField;

  const canSave = useMemo(() => {
    if (isSaving || isDeleting) return false;
    if (!v.question.trim()) return false;
    if (mode === "edit" && !tracking.isDirty) return false;
    return true;
  }, [isSaving, isDeleting, v.question, mode, tracking.isDirty]);

  const saveLabel = mode === "edit" ? "Save changes" : "Create FAQ";

  async function handleSave() {
    await onSave(v);
  }

  return (
    <div className="min-h-full bg-paper">
      <EditorTopBar
        question={v.question}
        showOnFaqPage={v.showOnFaqPage}
        isDirty={tracking.isDirty}
        isSaving={isSaving}
        saveLabel={saveLabel}
        canSave={canSave}
        onDiscard={mode === "edit" ? tracking.reset : undefined}
        onSave={handleSave}
      />

      <div className="grid min-h-[calc(100vh-3.5rem-var(--banner-h))] grid-cols-[1fr_320px] gap-0">
        {/* Main column */}
        <div className="flex min-w-0 flex-col">
          <div className="px-8 pb-6 pt-8">
            {saveError && (
              <div className="mb-3 rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
                {saveError}
              </div>
            )}
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
              Question
            </label>
            <input
              type="text"
              value={v.question}
              onChange={(e) => setField("question", e.target.value)}
              placeholder="What do clients commonly ask?"
              className="font-display w-full bg-transparent text-[28px] font-medium leading-tight tracking-tight text-ink placeholder:text-ink-5 focus:outline-none"
            />
          </div>

          <div className="flex-1 px-8 pb-12">
            <div className="hair-b mb-3 border-b pb-2 text-[11px] font-medium uppercase tracking-wider text-ink-4">
              Answer
            </div>
            <div className="shadow-card h-full overflow-hidden rounded-lg bg-paper-hi">
              <AnswerEditor
                value={v.answer}
                onChange={(next) => setField("answer", next)}
              />
            </div>
          </div>
        </div>

        {/* Right rail */}
        <aside className="hair-l border-l overflow-y-auto bg-canvas/30 px-4 py-6">
          <div className="space-y-3">
            <VisibilityPanel
              showOnFaqPage={v.showOnFaqPage}
              onChange={(next) => setField("showOnFaqPage", next)}
            />
            <SortOrderPanel
              sortOrder={v.sortOrder}
              onChange={(next) => setField("sortOrder", next)}
            />

            {mode === "edit" && onDelete && (
              <div className="hair rounded-lg border border-rust/30 bg-paper-hi p-4">
                <div className="text-[12.5px] font-medium text-ink-2">
                  Delete this FAQ
                </div>
                <p className="mt-1 text-[11.5px] text-ink-4">
                  Removes the FAQ from /faq and any landing pages it's
                  embedded in. Cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting || isSaving}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-rust/30 px-3 py-1.5 text-[12px] font-medium text-rust hover:bg-rust-s/40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 size={12} strokeWidth={2} />
                  {isDeleting ? "Deleting…" : "Delete FAQ"}
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export function emptyFaqFormValues(): FaqFormValues {
  return {
    question: "",
    answer: "",
    sortOrder: null,
    showOnFaqPage: true,
  };
}

export function faqFormValuesFromRecord(record: {
  question: string;
  answer: string | null;
  sortOrder: number | null;
  showOnFaqPage: boolean;
}): FaqFormValues {
  return {
    question: record.question,
    answer: record.answer ?? "",
    sortOrder: record.sortOrder,
    showOnFaqPage: record.showOnFaqPage,
  };
}
